/**
 * 容园 XD 协议扩展键（与 SDK keyCodes.ts SPECIAL_KEYS + MOUSE_KEYS 对齐）
 * 供改键源盘、UI 标签、写盘编码共用。
 */

import type { RongyuanUiKeyMeta, RongyuanXdKeyData } from "./rongyuanKeyCodes";
import {
  RONGYUAN_UI_DISABLED,
  registerRongyuanXdToXsMapping,
  rongyuanXdToXsUiCode,
} from "./rongyuanXsKeyBridge";

export { RONGYUAN_UI_DISABLED } from "./rongyuanXsKeyBridge";

export type RongyuanExtraKeyCategory =
  | "media"
  | "light"
  | "control"
  | "mouse"
  | "special";

export interface RongyuanSdkKeySource {
  name: string;
  value: number;
  page: string;
  param1?: number;
  param2?: number;
  param3?: number;
  category: RongyuanExtraKeyCategory;
  /** 为 false 时不进入改键源盘（如物理 FN 键） */
  inPalette?: boolean;
}

/** KeyCharacter → 线格式 [page, param1, value, param2] */
export function rongyuanSdkKeyCharacterToXd(
  k: Pick<
    RongyuanSdkKeySource,
    "page" | "value" | "param1" | "param2" | "param3"
  >
): RongyuanXdKeyData {
  const p1 = k.param1 ?? 0;
  const p2 = k.param2 ?? 0;
  const p3 = k.param3 ?? 0;
  const val = k.value;

  switch (k.page) {
    case "CONSUMER_KEY":
    case "MOUSE":
    case "PROFILE":
      return { page: p1, param1: p2, value: val, param2: p3 };
    case "FUNCTION":
      if (p1 === 0) {
        return { page: 0, param1: p2, value: val, param2: p3 };
      }
      return { page: p1, param1: p2, value: val, param2: p3 };
    case "DISABLED":
      return { page: 0, param1: 0, value: 0, param2: 0 };
    default:
      return { page: p1, param1: p2, value: val, param2: p3 };
  }
}

function defineFromSdk(source: RongyuanSdkKeySource): RongyuanUiKeyMeta {
  const xd = rongyuanSdkKeyCharacterToXd(source);
  const uiCode =
    source.page === "DISABLED"
      ? RONGYUAN_UI_DISABLED
      : rongyuanXdToXsUiCode(xd.page, xd.param1, xd.value, xd.param2);
  registerRongyuanXdToXsMapping(xd, uiCode);
  return { name: source.name, xd, uiCode };
}

/** 与 SDK resources/keyCodes.ts 保持一致（SPECIAL_KEYS + MOUSE_KEYS + 灯效/回报率） */
export const RONGYUAN_EXTRA_KEY_SOURCES: readonly RongyuanSdkKeySource[] = [
  // —— 多媒体 ——
  {
    name: "上一曲",
    value: 0xb6,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x00,
    category: "media",
  },
  {
    name: "下一曲",
    value: 0xb5,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x00,
    category: "media",
  },
  {
    name: "停止",
    value: 0xb7,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x00,
    category: "special",
  },
  {
    name: "播放/暂停",
    value: 0xcd,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x00,
    category: "media",
  },
  {
    name: "静音",
    value: 0xe2,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x00,
    category: "media",
  },
  {
    name: "音量减",
    value: 0xea,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x00,
    category: "media",
  },
  {
    name: "音量加",
    value: 0xe9,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x00,
    category: "media",
  },
  {
    name: "播放器",
    value: 0x83,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x01,
    category: "media",
  },
  {
    name: "计算器",
    value: 0x92,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x01,
    category: "media",
  },
  {
    name: "邮件",
    value: 0x8a,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x01,
    category: "media",
  },
  {
    name: "我的电脑",
    value: 0x94,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x01,
    category: "media",
  },
  {
    name: "搜索",
    value: 0x21,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x02,
    category: "media",
  },
  {
    name: "主页",
    value: 0x23,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x02,
    category: "media",
  },
  {
    name: "返回",
    value: 0x24,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x02,
    category: "media",
  },
  {
    name: "刷新",
    value: 0x27,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x02,
    category: "media",
  },
  {
    name: "亮度减",
    value: 0x70,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x00,
    category: "media",
  },
  {
    name: "亮度加",
    value: 0x6f,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x00,
    category: "media",
  },

  // —— FN（仅协议登记，不进改键源盘）——
  {
    name: "FN",
    value: 0x00,
    page: "FUNCTION",
    param1: 0x0a,
    param2: 0x01,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "Right FN",
    value: 0x01,
    page: "FUNCTION",
    param1: 0x0a,
    param2: 0x01,
    param3: 0x01,
    category: "control",
    inPalette: false,
  },
  {
    name: "锁定 Win",
    value: 0x00,
    page: "FUNCTION",
    param1: 0x0a,
    param2: 0x03,
    param3: 0x00,
    category: "control",
  },
  {
    name: "查询电量",
    value: 0x00,
    page: "FUNCTION",
    param1: 0x0a,
    param2: 0x08,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "恢复出厂设置",
    value: 0x00,
    page: "FUNCTION",
    param1: 0x0a,
    param2: 0x02,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "背光开关",
    value: 0x00,
    page: "FUNCTION",
    param1: 0x0a,
    param2: 0x0c,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },

  // —— 系统 / Profile / DPI ——
  {
    name: "FN锁屏",
    value: 0x00,
    page: "FUNCTION",
    param1: 0x0a,
    param2: 0x0d,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "呼出Siri",
    value: 0x2c,
    page: "FUNCTION",
    param1: 0x00,
    param2: 0xe0,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "切换输入法",
    value: 0xe3,
    page: "FUNCTION",
    param1: 0x12,
    param2: 0x00,
    param3: 0x2c,
    category: "control",
    inPalette: false,
  },
  {
    name: "缩小",
    value: 0xe3,
    page: "FUNCTION",
    param1: 0x00,
    param2: 0x00,
    param3: 0x2d,
    category: "special",
  },
  {
    name: "放大",
    value: 0xe3,
    page: "FUNCTION",
    param1: 0x00,
    param2: 0x00,
    param3: 0x2e,
    category: "special",
  },
  {
    name: "麦克风开关",
    value: 0xf1,
    page: "CONSUMER_KEY",
    param1: 0x03,
    param2: 0x00,
    param3: 0x00,
    category: "special",
  },
  {
    name: "锁屏",
    value: 0xe3,
    page: "FUNCTION",
    param1: 0x00,
    param2: 0x00,
    param3: 0x0f,
    category: "control",
    inPalette: false,
  },
  {
    name: "DPI循环",
    value: 0x00,
    page: "PROFILE",
    param1: 0x14,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "DPI+",
    value: 0x01,
    page: "PROFILE",
    param1: 0x14,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "DPI-",
    value: 0x02,
    page: "PROFILE",
    param1: 0x14,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "DPI Shift",
    value: 0x04,
    page: "PROFILE",
    param1: 0x14,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "Profile值",
    value: 0x00,
    page: "PROFILE",
    param1: 0x08,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "Profile+",
    value: 0x01,
    page: "PROFILE",
    param1: 0x08,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "Profile-",
    value: 0x02,
    page: "PROFILE",
    param1: 0x08,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "Profile循环",
    value: 0x03,
    page: "PROFILE",
    param1: 0x08,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "Profile切换",
    value: 0x04,
    page: "PROFILE",
    param1: 0x08,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "Profile切换2",
    value: 0x04,
    page: "PROFILE",
    param1: 0x08,
    param2: 0x00,
    param3: 0x01,
    category: "control",
    inPalette: false,
  },
  {
    name: "Profile切换3",
    value: 0x04,
    page: "PROFILE",
    param1: 0x08,
    param2: 0x00,
    param3: 0x02,
    category: "control",
    inPalette: false,
  },
  {
    name: "Profile切换4",
    value: 0x04,
    page: "PROFILE",
    param1: 0x08,
    param2: 0x00,
    param3: 0x03,
    category: "control",
    inPalette: false,
  },
  {
    name: "Profile切换5",
    value: 0x04,
    page: "PROFILE",
    param1: 0x08,
    param2: 0x00,
    param3: 0x04,
    category: "control",
    inPalette: false,
  },
  {
    name: "禁用",
    value: 0x00,
    page: "DISABLED",
    param1: 0x00,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },

  // —— 灯效 / 回报率（协议功能键，SDK keyCodes 未收录）——
  {
    name: "灯效开关",
    value: 0x00,
    page: "FUNCTION",
    param1: 0x0a,
    param2: 0x09,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },
  {
    name: "灯效选项+",
    value: 0x04,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x08,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },
  {
    name: "灯效选项-",
    value: 0x04,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x08,
    param3: 0x01,
    category: "light",
    inPalette: false,
  },
  {
    name: "灯效模式",
    value: 0x03,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x08,
    param3: 0x02,
    category: "light",
    inPalette: false,
  },
  {
    name: "灯条模式切换",
    value: 0x00,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x08,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },
  {
    name: "灯条颜色切换",
    value: 0x03,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x08,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },
  {
    name: "切换灯效",
    value: 0x00,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x01,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },
  {
    name: "灯光亮度+",
    value: 0x01,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x02,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },
  {
    name: "灯光亮度-",
    value: 0x02,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x02,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },
  {
    name: "灯光速度+",
    value: 0x01,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x03,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },
  {
    name: "灯光速度-",
    value: 0x02,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x03,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },
  {
    name: "灯光颜色切换",
    value: 0x01,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x05,
    param3: 0x00,
    category: "light",
    inPalette: false,
  },
  {
    name: "一键白光",
    value: 0x01,
    page: "FUNCTION",
    param1: 0x0d,
    param2: 0x05,
    param3: 0x02,
    category: "light",
    inPalette: false,
  },
  {
    name: "回报率+",
    value: 0x01,
    page: "FUNCTION",
    param1: 0x05,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "回报率-",
    value: 0x02,
    page: "FUNCTION",
    param1: 0x05,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },

  // —— page=0x0E 连接模式（FN 层数字行常见出厂映射）——
  {
    name: "USB模式",
    value: 0x00,
    page: "FUNCTION",
    param1: 0x0e,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "2.4G模式",
    value: 0x01,
    page: "FUNCTION",
    param1: 0x0e,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "蓝牙模式",
    value: 0x02,
    page: "FUNCTION",
    param1: 0x0e,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },
  {
    name: "连接切换",
    value: 0x05,
    page: "FUNCTION",
    param1: 0x0e,
    param2: 0x00,
    param3: 0x00,
    category: "control",
    inPalette: false,
  },

  // —— 鼠标 ——
  {
    name: "鼠标左键",
    value: 0xf0,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x00,
    category: "mouse",
  },
  {
    name: "鼠标右键",
    value: 0xf1,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x00,
    category: "mouse",
  },
  {
    name: "鼠标中键",
    value: 0xf2,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x00,
    category: "mouse",
  },
  {
    name: "鼠标后退",
    value: 0xf3,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x00,
    category: "mouse",
  },
  {
    name: "鼠标前进",
    value: 0xf4,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x00,
    category: "mouse",
  },
  {
    name: "滚轮左",
    value: 0xf5,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x00,
    category: "mouse",
    inPalette: false,
  },
  {
    name: "滚轮右",
    value: 0xf6,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x00,
    category: "mouse",
    inPalette: false,
  },
  {
    name: "滚轮前",
    value: 0xf7,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x00,
    category: "mouse",
    inPalette: false,
  },
  {
    name: "滚轮后",
    value: 0xf8,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x00,
    category: "mouse",
    inPalette: false,
  },
  {
    name: "滚轮向上",
    value: 0xf5,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x01,
    category: "mouse",
  },
  {
    name: "滚轮向下",
    value: 0xf5,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0xff,
    category: "mouse",
  },
  {
    name: "X轴向右",
    value: 0xf6,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x05,
    category: "mouse",
    inPalette: false,
  },
  {
    name: "X轴向左",
    value: 0xf6,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0xfb,
    category: "mouse",
    inPalette: false,
  },
  {
    name: "Y轴向下",
    value: 0xf7,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0x05,
    category: "mouse",
    inPalette: false,
  },
  {
    name: "Y轴向上",
    value: 0xf7,
    page: "MOUSE",
    param1: 0x01,
    param2: 0x00,
    param3: 0xfb,
    category: "mouse",
    inPalette: false,
  },
];

const PALETTE_SOURCES = RONGYUAN_EXTRA_KEY_SOURCES.filter(
  (s) => s.inPalette !== false
);

/** 全部协议扩展键元数据（含 inPalette:false，供 FN 层读回展示/标签） */
export const RONGYUAN_ALL_PROTOCOL_KEYS: readonly RongyuanUiKeyMeta[] =
  RONGYUAN_EXTRA_KEY_SOURCES.map(defineFromSdk);

/** 改键源盘可见的扩展键元数据 */
export const RONGYUAN_PROTOCOL_EXTRA_KEYS: readonly RongyuanUiKeyMeta[] =
  RONGYUAN_ALL_PROTOCOL_KEYS.filter((meta) =>
    PALETTE_SOURCES.some((source) => source.name === meta.name)
  );

const NAME_TO_UI = new Map(
  RONGYUAN_PROTOCOL_EXTRA_KEYS.map((m) => [m.name, m.uiCode])
);

function uiCodesFromNames(names: string[]): number[] {
  return names
    .map((name) => NAME_TO_UI.get(name))
    .filter((c): c is number => c !== undefined);
}

function matrixFromCategory(category: RongyuanExtraKeyCategory): number[][] {
  const names = PALETTE_SOURCES.filter((s) => s.category === category).map(
    (s) => s.name
  );
  return [uiCodesFromNames(names)];
}

/** 改键源盘：按容园协议分类的 UI 键码矩阵 */
export const RONGYUAN_EXTRA_KEYS_MATRIX = {
  media: matrixFromCategory("media"),
  special: matrixFromCategory("special"),
  mouse: matrixFromCategory("mouse"),
  macro: [
    [
      62720, 62721, 62722, 62723, 62724, 62725, 62726, 62727, 62728, 62729,
      62730, 62731, 62732, 62733, 62734, 62735,
    ],
  ],
};
